/**
 * CategoryNav
 *
 * Horizontal scrollable category shortcut strip matching Shapermint's
 * SwiperCollectionShortcuts design: round thumbnails, centered labels,
 * left/right navigation arrows.
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { useIntl } from '@/lib/intl';
import { useNavCollections } from '@/lib/hooks/use-nav-collections';

/* ---------- styled ------------------------------------------------------- */

const Wrapper = styled.div`
  position: relative;
  margin: 12px 0 8px;
  overflow: hidden;

  @media (max-width: 480px) {
    margin-top: 32px;
  }
`;

const Strip = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 4px 0;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 480px) {
    gap: 16px;
  }
`;

const ItemLink = styled(Link)<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  flex-shrink: 0;
  width: 118px;
`;

const Thumb = styled.div<{ $active: boolean }>`
  width: 118px;
  height: 163px;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  border: 2px solid ${({ $active }) => ($active ? '#292929' : 'transparent')};
  transition: border-color 0.2s;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Label = styled.span<{ $active: boolean }>`
  font-size: 12px;
  color: ${({ $active }) => ($active ? '#292929' : '#4b5563')};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  text-align: center;
  line-height: 1.25;
  white-space: nowrap;
`;

const NavButton = styled.button<{ $direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-60%);
  ${({ $direction }) => ($direction === 'left' ? 'left: 0;' : 'right: 0;')}
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #e5e7eb;
  background: white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  }

  svg {
    width: 14px;
    height: 14px;
    color: #292929;
  }
`;

const SkeletonThumb = styled.div`
  width: 118px;
  height: 163px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 400px 100%;

  @keyframes shimmer {
    0% { background-position: -200px 0; }
    100% { background-position: 200px 0; }
  }
  animation: shimmer 1.4s ease infinite;
`;

const SkeletonLabel = styled.div`
  width: 70px;
  height: 12px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
  background-size: 400px 100%;
  animation: shimmer 1.4s ease infinite;
`;

const SkeletonItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: 118px;
`;

/* ---------- component ---------------------------------------------------- */

interface CategoryNavProps {
  readonly currentSlug?: string;
}

export function CategoryNav({ currentSlug }: CategoryNavProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { localizedPath } = useIntl();
  const { collections, loading } = useNavCollections();

  const checkScroll = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollWidth > el.clientWidth + el.scrollLeft + 4);
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll]);

  const scrollLeft = () => {
    stripRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    stripRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  };

  return (
    <Wrapper>
      {canScrollLeft && (
        <NavButton $direction="left" onClick={scrollLeft} aria-label="Scroll categories left">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </NavButton>
      )}

      <Strip ref={stripRef} onScroll={checkScroll}>
        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
              <SkeletonItem key={i}>
                <SkeletonThumb />
                <SkeletonLabel />
              </SkeletonItem>
            ))
          : collections.map((cat) => {
              const isActive = currentSlug === cat.slug;
              const href = localizedPath(`/collections/${cat.slug}`);

              return (
                <ItemLink key={cat.slug} to={href} $active={isActive}>
                  <Thumb $active={isActive}>
                    {cat.imageUri && (
                      <img src={cat.imageUri} alt={cat.label} loading="lazy" />
                    )}
                  </Thumb>
                  <Label $active={isActive}>{cat.label}</Label>
                </ItemLink>
              );
            })}
      </Strip>

      {canScrollRight && (
        <NavButton $direction="right" onClick={scrollRight} aria-label="Scroll categories right">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </NavButton>
      )}
    </Wrapper>
  );
}

export default CategoryNav;
